import { useMemo } from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import { useGuestListRsvpProductRow } from './useGuestListRsvpProductRow';
import { useInvitationsProductRow } from './useInvitationsProductRow';
import { useRegistryProductRow } from './useRegistryProductRow';
import { useWebsiteProductRow } from './useWebsiteProductRow';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import { useIsContactCollectionMessageEnabled } from '@shared/hooks/useIsContactCollectionMessageEnabled';
import type { EventPageType, EventPageVisibility } from '@graphql/generated';

export type PageVisibility = `${EventPageVisibility}`;

export interface PageInfo {
  id: string;
  pageSlug: string;
  visibility: PageVisibility;
  type?: EventPageType;
  pageTitle?: string;
  createdAt?: {
    timestamp: string;
  };
  photo?: {
    id: string;
    url: string;
  } | null;
}
export function useProductRows(eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals, eventId?: string, pendingRefundOrdersCount?: number) {
  const { value: messagingEnableHotelBlockReminder } = useFeatureValue('messagingEnableHotelBlockReminder');
  const { value: enableRsvpReminderMessage } = useFeatureValue('enableRsvpReminderMessage');
  const [enableContactCollectionMessage] = useIsContactCollectionMessageEnabled();

  const websiteProductRow = useWebsiteProductRow(eventPages, productVerticals);
  const registryProductRow = useRegistryProductRow(eventPages, productVerticals, pendingRefundOrdersCount);
  const invitationsProductRow = useInvitationsProductRow({
    productVerticals,
    printThankYouCategoryEnabled: true,
    hotelBlockReminderEnabled: messagingEnableHotelBlockReminder === 'on',
    enableRsvpReminderMessage: enableRsvpReminderMessage === 'on',
    enableContactCollectionMessage,
    eventId,
  });
  const guestListRsvpProductRow = useGuestListRsvpProductRow(eventPages, productVerticals);

  const productRows = useMemo<ReadonlyArray<ProductRow>>(
    () =>
      [websiteProductRow, registryProductRow, invitationsProductRow, guestListRsvpProductRow].sort((a, b) => {
        return (b.isEnabled ? 1 : 0) - (a.isEnabled ? 1 : 0);
      }),
    [websiteProductRow, registryProductRow, invitationsProductRow, guestListRsvpProductRow],
  );

  return productRows;
}
